import React, { useState, useMemo } from 'react';
import {
  useOperationsActiveStaff,
  useKpiSummary,
  usePenaltyPresets,
  useEmployees,
  penaltiesAPI,
  QUERY_KEYS,
} from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, Input } from '@/components/ui';
import { BranchFilterSelect } from '@/components/common';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { formatAttendanceTime } from '../utils/datetime';
import {
  Users,
  AlertTriangle,
  BarChart2,
  Clock,
  CheckCircle2,
  RefreshCw,
  Building2,
  TrendingUp,
  DollarSign,
  Award,
  Activity,
  Eye,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type TabKey = 'live' | 'penalties' | 'kpi';

interface TabConfig {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabConfig[] = [
  { key: 'live',      label: "Jonli ko'rinish",  icon: <Activity      className="h-4 w-4" /> },
  { key: 'penalties', label: "Jarima qo'llash",  icon: <AlertTriangle className="h-4 w-4" /> },
  { key: 'kpi',       label: 'KPI',              icon: <BarChart2     className="h-4 w-4" /> },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a Date object as YYYY-MM-DD using LOCAL time (not UTC). */
const toLocalIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toDay = () => toLocalIso(new Date());

const getWeekMonday = (offset = 0) => {
  const d = new Date();
  const day = d.getDay(); // local day of week
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff + offset * 7);
  return toLocalIso(d); // local ISO, not UTC
};

const fmtTime = (ts: string | null | undefined) => {
  return formatAttendanceTime(ts, 'uz-UZ') ?? '–';
};

const fmtNum = (n: number | null | undefined, decimals = 0) => {
  if (n == null) return '–';
  return n.toLocaleString('uz-UZ', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtCurrency = (n: number | null | undefined) => {
  if (n == null) return '–';
  return `${fmtNum(n)} so'm`;
};

// ─── Metric card ──────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  icon,
  color = 'blue',
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'slate';
}) {
  const palette = {
    blue:   { bg: 'bg-blue-50',   icon: 'text-blue-500',   val: 'text-blue-800'   },
    green:  { bg: 'bg-green-50',  icon: 'text-green-500',  val: 'text-green-800'  },
    amber:  { bg: 'bg-amber-50',  icon: 'text-amber-500',  val: 'text-amber-800'  },
    red:    { bg: 'bg-red-50',    icon: 'text-red-500',    val: 'text-red-800'    },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-500', val: 'text-purple-800' },
    slate:  { bg: 'bg-slate-50',  icon: 'text-slate-400',  val: 'text-slate-700'  },
  };
  const c = palette[color];
  return (
    <div className={`rounded-2xl ${c.bg} p-4 flex items-start gap-3`}>
      <div className={`mt-0.5 shrink-0 ${c.icon}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 mb-1 leading-none">{label}</p>
        <p className={`text-2xl font-bold leading-none ${c.val}`}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-blue-600" />
    </div>
  );
}

function EmptyBox({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center py-16 text-slate-400 gap-3">
      <Eye className="h-10 w-10 opacity-25" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ─── LIVE TAB ─────────────────────────────────────────────────────────────────

function LiveTab({ branchId }: { branchId: string }) {
  const [snapshotAt, setSnapshotAt] = useState('');
  const { data, isLoading, refetch, isFetching } = useOperationsActiveStaff({
    branch_id: branchId,
    at: snapshotAt,
  });

  const payload   = (data as any)?.data ?? {};
  const staff     = Array.isArray(payload.active_staff) ? payload.active_staff : [];
  const snapshotTs = payload.at ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="block text-xs text-slate-500 mb-1">Vaqt (ixtiyoriy — bo'sh qoldirsangiz hozirgi vaqt)</label>
              <Input
                type="datetime-local"
                value={snapshotAt}
                onChange={(e) => setSnapshotAt(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
              Yangilash
            </Button>
          </div>
          {snapshotTs && (
            <p className="mt-2 text-xs text-slate-400">
              Ko'rinish vaqti: <span className="font-mono">{snapshotTs}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard label="Hozir ishda" value={staff.length} icon={<Users className="h-5 w-5" />} color="green" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Hozir ishda bo'lgan xodimlar ({staff.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-0 py-0">
          {isLoading ? (
            <Spinner />
          ) : staff.length === 0 ? (
            <EmptyBox message="Hozirda faol xodimlar yo'q" />
          ) : (
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Xodim</th>
                    <th>Filial</th>
                    <th>Smena</th>
                    <th>Kirish vaqti</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((row: any, idx: number) => (
                    <tr key={row.id ?? idx}>
                      <td className="text-slate-400 text-xs">{idx + 1}</td>
                      <td>
                        <div className="font-medium text-slate-900 text-sm">
                          {row.employee?.full_name ?? row.full_name ?? row.name ?? '–'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {row.employee?.employee_code ?? row.employee_code ?? ''}
                        </div>
                      </td>
                      <td className="text-sm text-slate-600">
                        {row.branch?.name ?? row.employee?.branch?.name ?? '–'}
                      </td>
                      <td className="text-sm">
                        {row.shift?.name ?? row.current_shift?.name ?? '–'}
                      </td>
                      <td className="ui-nowrap font-mono text-sm">
                        {fmtTime(row.checked_in_at ?? row.first_in_at)}
                      </td>
                      <td>
                        <Badge variant="success" className="flex items-center gap-1 w-fit">
                          <CheckCircle2 className="h-3 w-3" />
                          Ishda
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── PENALTIES TAB ────────────────────────────────────────────────────────────

function PenaltiesTab({ branchId }: { branchId: string }) {
  const [employeeId, setEmployeeId] = useState('');
  const [workDate,   setWorkDate]   = useState(toDay());
  const [notes,      setNotes]      = useState('');
  const [search,     setSearch]     = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: employeesRaw } = useEmployees({ per_page: 500, branch_id: branchId });
  const { data: presetsRaw }   = usePenaltyPresets({ is_active: true, branch_id: branchId });

  const employees: any[] = useMemo(() => {
    const d = (employeesRaw as any)?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [employeesRaw]);

  const allPresets: any[] = useMemo(() => {
    const d = (presetsRaw as any)?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [presetsRaw]);

  const filteredPresets = useMemo(() => {
    if (!search.trim()) return allPresets;
    const q = search.toLowerCase();
    return allPresets.filter(
      (p: any) => p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q),
    );
  }, [allPresets, search]);

  const selected = useMemo(
    () => allPresets.filter((p: any) => selectedIds.includes(p.id)),
    [allPresets, selectedIds],
  );

  const totalAmount = selected.reduce((acc: number, p: any) => acc + (p.amount ?? 0), 0);

  const applyMutation = useMutation({
    mutationFn: () =>
      penaltiesAPI.applyChecklist({
        employee_id: Number(employeeId),
        work_date:   workDate,
        preset_ids:  selectedIds,
        notes:       notes || undefined,
      }),
    onSuccess: () => {
      toast.success("Jarimalar qo'llandi!");
      setSelectedIds([]);
      setNotes('');
    },
    onError: () => toast.error('Xato yuz berdi'),
  });

  const togglePreset = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Jarima ma'lumotlari</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Xodim *</label>
              <select
                className="min-h-[2.75rem] w-full rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">Xodimni tanlang</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name ?? emp.name} ({emp.employee_code ?? emp.code ?? emp.id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Ish sanasi *</label>
              <Input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Izoh (ixtiyoriy)</label>
              <textarea
                className="min-h-[80px] w-full rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Izoh kiriting..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {selected.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-1">
                <p className="text-xs font-semibold text-amber-700">Tanlangan jarimalar:</p>
                {selected.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between text-xs text-amber-800">
                    <span>{p.name}</span>
                    <span className="font-mono">{fmtCurrency(p.amount)}</span>
                  </div>
                ))}
                <div className="border-t border-amber-200 pt-1 mt-1 flex justify-between text-sm font-bold text-amber-900">
                  <span>Jami:</span>
                  <span>{fmtCurrency(totalAmount)}</span>
                </div>
              </div>
            )}
            <Button
              className="w-full"
              disabled={!employeeId || selectedIds.length === 0 || applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {applyMutation.isPending ? "Qo'llanmoqda…" : `Jarima qo'llash (${selectedIds.length} ta)`}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Jarima shablonlar ({filteredPresets.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Qidirish..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredPresets.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Shablon topilmadi</p>
              ) : (
                filteredPresets.map((preset: any) => {
                  const isSelected = selectedIds.includes(preset.id);
                  return (
                    <label
                      key={preset.id}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePreset(preset.id)}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">{preset.name}</div>
                        {preset.code && <div className="text-xs text-slate-400">{preset.code}</div>}
                      </div>
                      <div className="shrink-0 text-sm font-semibold text-red-600">
                        {fmtCurrency(preset.amount)}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── KPI TAB ──────────────────────────────────────────────────────────────────

function KpiTab({ branchId }: { branchId: string }) {
  const getFirstDay = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  };
  const [dateFrom, setDateFrom] = useState(getFirstDay);
  const [dateTo,   setDateTo]   = useState(toDay);

  const enabled = !!dateFrom && !!dateTo;
  const { data, isLoading } = useKpiSummary(
    { date_from: dateFrom, date_to: dateTo, branch_id: branchId },
    enabled,
  );

  const payload     = (data as any)?.data ?? {};
  const summary     = payload.summary ?? {};
  const rows: any[] = Array.isArray(payload.rows) ? payload.rows : [];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Boshlanish</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="max-w-[180px]" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Tugash</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="max-w-[180px]" />
            </div>
          </div>
        </CardContent>
      </Card>

      {!isLoading && summary.employees != null && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <MetricCard label="Xodimlar"      value={fmtNum(summary.employees)}                   icon={<Users        className="h-5 w-5" />} color="blue"   />
          <MetricCard label="Ish soati"     value={fmtNum(summary.total_worked_hours, 1)}        icon={<Clock        className="h-5 w-5" />} color="slate"  />
          <MetricCard label="Savdo soni"    value={fmtNum(summary.total_sales_quantity)}         icon={<TrendingUp   className="h-5 w-5" />} color="green"  />
          <MetricCard label="Savdo summasi" value={fmtCurrency(summary.total_sales_amount)}      icon={<DollarSign   className="h-5 w-5" />} color="green"  />
          <MetricCard label="Jarimalar"     value={fmtCurrency(summary.total_penalties_amount)}  icon={<AlertTriangle className="h-5 w-5" />} color="red"   />
          <MetricCard label="Bonus"         value={fmtCurrency(summary.total_bonus_amount)}      icon={<Award        className="h-5 w-5" />} color="purple" />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>KPI jadvali ({rows.length} ta xodim)</CardTitle>
        </CardHeader>
        <CardContent className="px-0 py-0">
          {isLoading ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <EmptyBox message="KPI ma'lumoti topilmadi" />
          ) : (
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Xodim</th>
                    <th className="text-right">Ish soati</th>
                    <th className="text-right">Savdo soni</th>
                    <th className="text-right">Savdo summasi</th>
                    <th className="text-right">Jarimalar</th>
                    <th className="text-right">Bonus</th>
                    <th className="text-right">Sof summa</th>
                    <th className="text-right">KPI ball</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: any, idx: number) => (
                    <tr key={row.employee_id ?? idx}>
                      <td className="text-slate-400 text-xs">{idx + 1}</td>
                      <td>
                        <div className="font-medium text-sm text-slate-900">{row.employee_name ?? '–'}</div>
                        <div className="text-xs text-slate-400">{row.employee_code ?? ''}</div>
                      </td>
                      <td className="text-right text-sm">{fmtNum(row.worked_hours, 1)}</td>
                      <td className="text-right text-sm">{fmtNum(row.sales_quantity)}</td>
                      <td className="text-right text-sm font-medium">{fmtCurrency(row.sales_amount)}</td>
                      <td className="text-right text-sm text-red-600">
                        {row.penalties_amount ? fmtCurrency(row.penalties_amount) : '–'}
                      </td>
                      <td className="text-right text-sm text-emerald-600">
                        {row.bonus_amount ? fmtCurrency(row.bonus_amount) : '–'}
                      </td>
                      <td className="text-right text-sm font-semibold">{fmtCurrency(row.net_amount)}</td>
                      <td className="text-right">
                        {row.kpi_score != null ? (
                          <Badge
                            variant={row.kpi_score >= 80 ? 'success' : row.kpi_score >= 50 ? 'warning' : 'destructive'}
                          >
                            {fmtNum(row.kpi_score, 1)}
                          </Badge>
                        ) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function OperationsCenterPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('live');
  const [branchId, setBranchId]   = useState('');

  return (
    <div className="page-shell">
      <div className="page-toolbar">
        <div>
          <h1>Operatsiyalar markazi</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Jonli ko'rinish · Jarimalar · KPI
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <BranchFilterSelect
            alwaysShow
            className="min-h-[2.25rem] rounded-xl border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={branchId}
            onChange={setBranchId}
            allLabel="Barcha filiallar"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 pb-0">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'live'      && <LiveTab      branchId={branchId} />}
        {activeTab === 'penalties' && <PenaltiesTab branchId={branchId} />}
        {activeTab === 'kpi'       && <KpiTab       branchId={branchId} />}
      </div>
    </div>
  );
}
